import React from 'react';
import { X, AlertTriangle, RefreshCw, Trash2, Image as ImageIcon } from 'lucide-react';
import { useSyncStore } from '../lib/offline/sync-store';
import {
  retryFailed,
  discardFailedMutation,
  discardFailedUpload,
} from '../lib/offline/sync-counts';
import { Tooltip } from './ui/tooltip';

/**
 * What a hand sees when something did not save.
 *
 * The old behaviour was that they saw nothing: a mutation that failed three
 * times left both the flush and `getPendingCount()`, the orange "N changes
 * waiting to sync" bar disappeared, and a disappearing pending bar is the only
 * "you are caught up" signal this app has. Silence meant success and silence
 * also meant loss.
 *
 * So this panel exists to say the opposite out loud, per item: what it was,
 * when they entered it, and what the server said. Retry is offered because the
 * usual cause is fixable (they have driven back into coverage). Discard is
 * offered — with a confirm — because the alternative to letting them delete it
 * deliberately is a red bar they can never clear, and a warning nobody can
 * clear is a warning everybody learns to ignore.
 */
interface Props {
  onClose: () => void;
}

function mutationLabel(entity: string, action: string): string {
  const noun = entity.replace(/s$/, '');
  const verb = action === 'create' ? 'New' : action === 'delete' ? 'Deleted' : 'Edited';
  return `${verb} ${noun}`;
}

const FailedSyncPanel: React.FC<Props> = ({ onClose }) => {
  const { failedMutationDetails, failedUploadDetails, isOnline } = useSyncStore();

  const total = failedMutationDetails.length + failedUploadDetails.length;
  if (total === 0) return null;

  const confirmDiscard = (what: string) =>
    window.confirm(`Discard ${what}? This cannot be undone and it will never be saved.`);

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-fieldiq-card border border-fieldiq-border rounded-t-xl sm:rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-fieldiq-border sticky top-0 bg-fieldiq-card z-10">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-400" />
            <h2 className="text-base font-bold text-white">Not saved ({total})</h2>
          </div>
          <div className="flex items-center gap-2">
            <Tooltip content={isOnline ? 'Try all again' : 'Connect to try again'}>
              <button
                onClick={() => retryFailed()}
                disabled={!isOnline}
                className="flex items-center gap-1.5 text-xs text-fieldiq-accent hover:text-amber-300 font-medium disabled:opacity-40"
              >
                <RefreshCw size={13} /> Try again
              </button>
            </Tooltip>
            <button onClick={onClose} className="text-fieldiq-muted hover:text-white">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-xs text-fieldiq-text">
            These did not reach the server and have stopped retrying. They are still on this
            device. Try again once you have signal — if it keeps failing, re-enter the work or
            tell your supervisor.
          </p>

          {failedMutationDetails.map((m) => (
            <div
              key={m.clientMutationId}
              className="bg-fieldiq-dark rounded-lg border border-red-500/30 p-3"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white">
                    {mutationLabel(m.entity, m.action)}
                  </p>
                  <p className="text-[10px] text-fieldiq-muted">
                    {new Date(m.timestamp).toLocaleString()} · {m.retryCount} attempt
                    {m.retryCount === 1 ? '' : 's'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (confirmDiscard(mutationLabel(m.entity, m.action).toLowerCase())) {
                      void discardFailedMutation(m.clientMutationId);
                    }
                  }}
                  className="text-xs text-fieldiq-muted hover:text-red-400 flex items-center gap-1 shrink-0"
                >
                  <Trash2 size={12} /> Discard
                </button>
              </div>
              {m.lastError && <p className="text-xs text-red-400">{m.lastError}</p>}
            </div>
          ))}

          {failedUploadDetails.map((u) => (
            <div key={u.id} className="bg-fieldiq-dark rounded-lg border border-red-500/30 p-3">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-start gap-2 min-w-0">
                  {u.localUrl ? (
                    <img
                      src={u.localUrl}
                      alt={u.fileName}
                      className="w-10 h-10 rounded object-cover shrink-0"
                    />
                  ) : (
                    <ImageIcon size={18} className="text-fieldiq-muted shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{u.fileName}</p>
                    <p className="text-[10px] text-fieldiq-muted">
                      {new Date(u.createdAt).toLocaleString()} · {u.retryCount} attempt
                      {u.retryCount === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (confirmDiscard(`the photo "${u.fileName}"`)) {
                      void discardFailedUpload(u.id);
                    }
                  }}
                  className="text-xs text-fieldiq-muted hover:text-red-400 flex items-center gap-1 shrink-0"
                >
                  <Trash2 size={12} /> Discard
                </button>
              </div>
              {u.lastError && <p className="text-xs text-red-400">{u.lastError}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FailedSyncPanel;
